import { Link, useParams, Navigate } from "react-router-dom";
import { useMemo } from "react";
import { ArrowRight, CircleHelp } from "lucide-react";
import { Seo, faqJsonLd } from "@/components/Seo";
import { PageHead, Section, Reveal, CtaBand, QaBlock } from "@/components/Shared";
import { UI, PATHS } from "@/i18n/ui";
import { TOPIC_LIST, TOPIC_PAGES, findTopic } from "@/data/topics";
import { ANSWERS, findAnswer } from "@/data/answers";
import { SERVICES } from "@/data/services";

const OTHER_TOPICS = TOPIC_LIST.filter((x) => !x.hasPage);

export function MentalHealthPage({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const meta = {
    id: { title: "Edukasi Kesehatan Mental | Bali Empower Denpasar", desc: "Pusat edukasi kesehatan mental Bali Empower: kecemasan, stres, burnout, trauma, kepercayaan diri, dan konflik keluarga — dijelaskan secara profesional dan bertanggung jawab." },
    en: { title: "Mental Health Education Hub | Bali Empower Denpasar", desc: "Bali Empower's mental health education hub: anxiety, stress, burnout, trauma, self-esteem and family conflict — explained professionally and responsibly." },
  }[lang];
  const pages = Object.values(TOPIC_PAGES);
  return (
    <div data-testid="mental-health-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.mentalHealth} title={t.sections.problems}
        sub={lang === "id"
          ? "Pelajari topik kesehatan mental yang paling sering dialami, ditulis secara bertanggung jawab untuk membantu Anda memahami kapan dukungan profesional dibutuhkan."
          : "Learn about the most common mental health topics, written responsibly to help you understand when professional support may be needed."} />
      <Section className="pt-0">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((tp, i) => (
            <Reveal key={tp.slug.en} delay={i * 0.04}>
              <Link to={p.topic(tp.slug[lang])} data-testid={`mh-topic-card-${tp.slug.en}`}
                className="be-card block h-full rounded-2xl border border-[#E5E1D8] bg-white p-8">
                <h2 className="text-xl font-display text-[#1F2937]">{tp.title[lang]}</h2>
                <p className="mt-2 text-sm text-[#4A6B53] font-medium">{tp.question[lang]}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#4B5563] line-clamp-3">{tp.answer[lang]}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                  {t.cta.learnMore} <ArrowRight size={14} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-14">
          <h2 className="text-2xl font-display text-[#1F2937]">
            {lang === "id" ? "Topik lain yang kami tangani" : "Other concerns we support"}
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {OTHER_TOPICS.map((x) => (
              <span key={x.en} className="rounded-full bg-white border border-[#E5E1D8] px-5 py-2.5 text-sm text-[#4B5563]">{x[lang]}</span>
            ))}
          </div>
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

export function TopicDetail({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const { slug } = useParams();
  const tp = findTopic(lang, slug);
  const jsonLd = useMemo(
    () => (tp ? [faqJsonLd([[tp.question[lang], tp.answer[lang]]])] : []),
    [tp, lang]
  );
  if (!tp) return <Navigate to={p.mentalHealth} replace />;
  const service = SERVICES.find((s) => s.key === tp.serviceKey);
  const meta = {
    id: { title: `${tp.title.id}: ${tp.question.id} | Bali Empower`, desc: tp.answer.id.slice(0, 155) },
    en: { title: `${tp.title.en}: ${tp.question.en} | Bali Empower`, desc: tp.answer.en.slice(0, 155) },
  }[lang];
  return (
    <div data-testid={`topic-detail-${tp.slug.en}`}>
      <Seo title={meta.title} description={meta.desc} lang={lang} jsonLd={jsonLd} />
      <PageHead eyebrow={t.nav.mentalHealth} title={tp.question[lang]} />
      <Section className="pt-0">
        <div className="max-w-3xl">
          <div className="rounded-2xl bg-[#4A6B53]/8 border border-[#4A6B53]/20 p-8" data-testid="topic-short-answer">
            <p className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-[#4A6B53] mb-3">{t.misc.shortAnswer}</p>
            <p className="text-[#1F2937] leading-relaxed">{tp.answer[lang]}</p>
          </div>
          <QaBlock q={t.misc.signs}>
            <ul className="list-disc pl-5 space-y-1.5">
              {tp.signs[lang].map((s) => <li key={s}>{s}</li>)}
            </ul>
          </QaBlock>
          <QaBlock q={t.misc.whenSeek}><p>{tp.whenToSeek[lang]}</p></QaBlock>
          <QaBlock q={t.misc.howHelp}>
            <p>{service.what[lang]}</p>
            <Link to={p.service(service.slug[lang])} data-testid="topic-related-service"
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-[#4A6B53] hover:underline">
              {t.misc.relatedService}: {service.name[lang]} <ArrowRight size={14} />
            </Link>
          </QaBlock>
          <p className="mt-8 text-xs text-[#4B5563] italic">
            {lang === "id"
              ? "Konten ini bersifat edukatif dan bukan pengganti konsultasi klinis individual. [Penulis & peninjau klinis akan dilengkapi oleh tim Bali Empower]"
              : "This content is educational and not a substitute for individual clinical consultation. [Author & clinical reviewer to be completed by the Bali Empower team]"}
          </p>
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

export function AnswersPage({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const meta = {
    id: { title: "Tanya Jawab Psikologi & Kesehatan Mental | Bali Empower", desc: "Jawaban langsung dan bertanggung jawab untuk pertanyaan umum tentang psikolog, konseling, asesmen psikologi, dan kesehatan mental di Denpasar, Bali." },
    en: { title: "Psychology & Mental Health Answers | Bali Empower", desc: "Direct, responsible answers to common questions about psychologists, counseling, psychological assessment and mental health in Denpasar, Bali." },
  }[lang];
  return (
    <div data-testid="answers-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.answers} title={t.nav.answers}
        sub={lang === "id"
          ? "Setiap halaman menjawab satu pertanyaan dengan jawaban singkat langsung, diikuti penjelasan mendalam."
          : "Each page answers one question with a short direct answer, followed by a detailed explanation."} />
      <Section className="pt-0">
        <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
          {ANSWERS.map((a, i) => (
            <Reveal key={a.slug.en} delay={i * 0.04}>
              <Link to={p.answer(a.slug[lang])} data-testid={`answer-card-${a.slug.en}`}
                className="be-card flex gap-4 h-full rounded-2xl border border-[#E5E1D8] bg-white p-7">
                <CircleHelp size={22} className="shrink-0 text-[#4A6B53]" strokeWidth={1.6} />
                <div>
                  <h2 className="font-display text-lg text-[#1F2937] leading-snug">{a.q[lang]}</h2>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                    {t.misc.readAnswer} <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

export function AnswerDetail({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const { slug } = useParams();
  const a = findAnswer(lang, slug);
  const jsonLd = useMemo(
    () => (a ? [faqJsonLd([[a.q[lang], a.short[lang]]])] : []),
    [a, lang]
  );
  if (!a) return <Navigate to={p.answers} replace />;
  const meta = {
    id: { title: `${a.q.id} | Bali Empower`, desc: a.short.id.slice(0, 155) },
    en: { title: `${a.q.en} | Bali Empower`, desc: a.short.en.slice(0, 155) },
  }[lang];
  return (
    <div data-testid={`answer-detail-${a.slug.en}`}>
      <Seo title={meta.title} description={meta.desc} lang={lang} jsonLd={jsonLd} />
      <PageHead eyebrow={t.nav.answers} title={a.q[lang]} />
      <Section className="pt-0">
        <div className="max-w-3xl">
          <div className="rounded-2xl bg-[#4A6B53]/8 border border-[#4A6B53]/20 p-8" data-testid="answer-short">
            <p className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-[#4A6B53] mb-3">{t.misc.shortAnswer}</p>
            <p className="text-[#1F2937] leading-relaxed">{a.short[lang]}</p>
          </div>
          <QaBlock q={t.misc.detail}><p>{a.detail[lang]}</p></QaBlock>
          <QaBlock q={t.misc.whenSeek}><p>{a.seek[lang]}</p></QaBlock>
          <QaBlock q={t.misc.howHelp}>
            <p>{lang === "id"
              ? "Bali Empower menyediakan konseling psikologi, asesmen psikologi, dan program kesehatan mental terstruktur di Renon, Denpasar Selatan, Bali."
              : "Bali Empower provides psychological counseling, psychological assessment and structured mental health programs in Renon, Denpasar Selatan, Bali."}</p>
            <Link to={p.services} data-testid="answer-services-link"
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-[#4A6B53] hover:underline">
              {t.cta.explore} <ArrowRight size={14} />
            </Link>
          </QaBlock>
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}
